import { useEffect, useState } from 'react';

import { useSearchParams, useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import Card from '#globalComponents/Card/Card';

import { calendarKeys } from '../../models/calendar/keys';

/**
 * Handles the redirect back from Google/Microsoft Calendar OAuth flow.
 * The backend sets ?success=true&provider=google|microsoft or ?error=<code>.
 */
const CalendarOAuthCallbackPage = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [status, setStatus] = useState('processing');
	const [errorMessage, setErrorMessage] = useState('');

	const provider = searchParams.get('provider');
	const providerLabel = provider === 'google' ? 'Google Calendar' : 'Microsoft 365';

	useEffect(() => {
		const success = searchParams.get('success');
		const error = searchParams.get('error');

		if (success === 'true') {
			setStatus('success');
			qc.invalidateQueries({ queryKey: calendarKeys.all });
			setTimeout(() => navigate('/crm/settings', { replace: true }), 1500);
		} else if (error) {
			setStatus('error');
			const messages = {
				invalid_state: 'Die Sitzung ist abgelaufen. Bitte versuchen Sie es erneut.',
				missing_parameters: 'Fehlende Parameter in der Antwort vom Provider.',
				oauth_failed: 'Die Autorisierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
				access_denied: 'Der Zugriff wurde verweigert. Bitte erlauben Sie den Kalender-Zugriff.',
			};
			setErrorMessage(messages[error] || `Fehler bei der Autorisierung: ${error}`);
		} else {
			navigate('/crm/settings', { replace: true });
		}
	}, [searchParams, navigate, qc]);

	return (
		<div className="min-h-screen flex items-center justify-center bg-bgBase-300">
			<Card className="max-w-md w-full p-8">
				<div className="text-center">
					{status === 'processing' && (
						<>
							<div className="mx-auto h-16 w-16 mb-4">
								<div className="animate-spin h-16 w-16 border-4 border-primary-500 border-t-transparent rounded-full" />
							</div>
							<h2 className="text-lg font-semibold text-text mb-2">Verarbeite Autorisierung</h2>
							<p className="text-sm text-text">Kalender wird verbunden…</p>
						</>
					)}

					{status === 'success' && (
						<>
							<div className="mx-auto h-16 w-16 mb-4 flex items-center justify-center">
								<svg
									className="h-16 w-16 text-success-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<h2 className="text-lg font-semibold text-text mb-2">{providerLabel} verbunden!</h2>
							<p className="text-sm text-text">Sie werden weitergeleitet…</p>
						</>
					)}

					{status === 'error' && (
						<>
							<div className="mx-auto h-16 w-16 mb-4 flex items-center justify-center">
								<svg
									className="h-16 w-16 text-warning-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<h2 className="text-lg font-semibold text-text mb-2">Verbindung fehlgeschlagen</h2>
							<p className="text-sm text-text mb-4">{errorMessage}</p>
							<button
								onClick={() => navigate('/crm/settings', { replace: true })}
								className="text-sm text-primary-600 hover:text-primary-700 font-medium"
							>
								Zurück zu den Einstellungen
							</button>
						</>
					)}
				</div>
			</Card>
		</div>
	);
};

export default CalendarOAuthCallbackPage;
